// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { dayCountSchema } from '@/billing/api/get-invoice-lifecycle-listing';
import { currencyAmountSchema } from '@/currency/api/types';
import { isoCurrencyCodeSchema } from '@/currency/constants/iso';
import { z } from 'zod';

// A single segment's aggregate for the billing lifecycle report's summary
// cards. avgDays is null when no invoice in the reporting window contributed
// an observation for the segment (the card renders an em-dash in that case);
// the carrying-cost fields are null in the same scenario since there's no
// input to compound. Carrying cost comes pre-computed from the backend
// (amount × (annualFactor^(avgDays / 365) − 1)) so the FE never re-derives
// it. Negative values represent carrying gains. Every segment — including
// date lag and total cycle — reports a real carrying cost derived from
// its own dollar-weighted population.
//
// avgDays parses into a number and the carrying costs into Decimals, failing
// the response when unparseable. The dollar-cap /
// non-negativity refines in dollarStringSchema are meant for outbound
// form submissions, not for inbound server responses where the server is
// the source of truth for format and bounds.
const invoiceLifecycleSegmentSummarySchema = z.object({
  avgDays: dayCountSchema.nullable(),
  invoiceCount: z.number(),
  totalCarryingCost: currencyAmountSchema.nullable(),
  annualizedCarryingCost: currencyAmountSchema.nullable(),
});

// Six-segment summary + total cycle aggregate + p90 cycle days (sizes the
// aggregate Gantt chart's calendar-day axis), all for a single invoice
// currency.
const currencyInvoiceLifecycleSummarySchema = z.object({
  isoCode: z.preprocess(
    (v) => (typeof v === 'string' ? v.toUpperCase() : v),
    isoCurrencyCodeSchema
  ),
  chargeLag: invoiceLifecycleSegmentSummarySchema,
  invoiceReview: invoiceLifecycleSegmentSummarySchema,
  deliveryLag: invoiceLifecycleSegmentSummarySchema,
  dateLag: invoiceLifecycleSegmentSummarySchema,
  actualTerms: invoiceLifecycleSegmentSummarySchema,
  delinquency: invoiceLifecycleSegmentSummarySchema,
  totalCycle: invoiceLifecycleSegmentSummarySchema,
  p90CycleDays: z.number().nullable(),
});

export type CurrencyInvoiceLifecycleSummary = z.infer<
  typeof currencyInvoiceLifecycleSummarySchema
>;

// The lifecycle summary is bucketed per invoice currency — carrying costs
// and dollar-weighted means are computed within each denomination and stack
// independently on the report. There is no cross-currency total.
const invoiceLifecycleSummarySchema = z.object({
  byCurrency: z
    .array(currencyInvoiceLifecycleSummarySchema)
    .nullable()
    .transform((v) => v ?? []),
});

type InvoiceLifecycleSummary = z.infer<typeof invoiceLifecycleSummarySchema>;

const invoiceLifecycleSummaryResponseSchema = z.object({
  data: invoiceLifecycleSummarySchema,
  metadata: responseMetadataSchema,
});

export interface InvoiceLifecycleFilter {
  windowDays?: number;
  customerKeys?: string[];
  projectKeys?: string[];
}

function serializeFilter(filter: InvoiceLifecycleFilter): string {
  const params = new URLSearchParams();
  if (filter.windowDays !== undefined) {
    params.set('windowDays', String(filter.windowDays));
  }
  for (const key of filter.customerKeys ?? []) {
    params.append('customerKey', key);
  }
  for (const key of filter.projectKeys ?? []) {
    params.append('projectKey', key);
  }
  return params.toString();
}

export async function getInvoiceLifecycleSummary(
  filter: InvoiceLifecycleFilter
): Promise<InvoiceLifecycleSummary> {
  const qs = serializeFilter(filter);
  const url =
    qs.length > 0
      ? `/v1/invoice-summary/lifecycle?${qs}`
      : '/v1/invoice-summary/lifecycle';
  const response = await api.get(url);
  const result = await parseResponse(
    invoiceLifecycleSummaryResponseSchema,
    response.data,
    { schemaName: 'InvoiceLifecycleSummary', endpoint: url }
  );
  return result.data;
}
